import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'umi';
import { Justify, SearchBox } from 'tea-component';
import FilterCommon, { useFilterCommon } from '@opm/components/FilterCommon';
import { useFilterCondData } from '@opm/components/FilterCommon/hooks/useFilterCondData';
import CardCommon from '@op/components/CardCommon';
import MarketContentBody from '@opm/components/MarketContentBody';
import { getChartTimeParams } from '@op/utils/time';
import { DBOM_POINT } from '@opm/operationLog/buriedPoint';
import useIntls from '@opm/hooks/useIntls';
import type { IHashtagTabProps } from '../types';
import {
  getHashtagSourcesCountryRegionOptions,
  HashtagCountryRegion,
} from '@opm/components/FilterCommon/config/Hashtag';
import {
  BURIED_CHANNEL,
  X_HASHTAG_COND_SOURCE,
} from './constants';
import { useStableValues } from '@opm/utils/stableKey';
import { buildApiFilter, getInitialSubFilter } from './subFilterUtils';
import XHashtagMatrixTable from './XHashtagMatrixTable';
import XHashtagToolbar from './XHashtagToolbar';
import type { IXHashtagSubFilter } from './types';
import './index.scss';
import { UTCDateFilterTips } from '@op/components/BubbleCommon';

const FILTER_INSTANCE_ID = 'hashtag-twitter';
const MIN_HEIGHT = 360;
const SEARCH_DEBOUNCE_MS = 300;

export default function TwitterTab({ backCondConfig }: IHashtagTabProps) {
  const dispatch = useDispatch();
  const { intl } = useIntls();
  const [hasDownload, setHasDownload] = useState(false);
  const [subFilter, setSubFilter] = useState<IXHashtagSubFilter>(getInitialSubFilter);
  const [searchInput, setSearchInput] = useState('');
  const downloadRef = useRef<(() => void) | null>(null);
  const isFirstRequestRef = useRef(true);

  const { filter, filterRef, setFilter, ...filterProps } = useFilterCommon({
    keys: ['MultiDateRange'],
    dependencyOrder: ['MultiDateRange'],
    instanceId: FILTER_INSTANCE_ID,
  });

  const { loading: condLoading, condData } = useFilterCondData({
    keys: [],
    extrRequestKeys: [HashtagCountryRegion.requestKey!],
    backCondConfig,
  });

  const countryOptions = useMemo(
    () =>
      getHashtagSourcesCountryRegionOptions(
        condData[HashtagCountryRegion.requestKey!],
        X_HASHTAG_COND_SOURCE,
      ),
    [condData],
  );

  const handleHasDataDate = useCallback(
    (date: string) => {
      const datePayload = getChartTimeParams(date, 'daily');
      dispatch({
        type: 'opinionMarketingFilter/UPDATE_FILTER',
        payload: {
          ...datePayload,
          date_type: 'daily',
          UPDATE: true,
        },
        instanceId: FILTER_INSTANCE_ID,
      });
    },
    [dispatch, filterRef],
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const nextSearchText = searchInput.trim();
      setSubFilter((prev) =>
        prev.search_text === nextSearchText
          ? prev
          : { ...prev, search_text: nextSearchText },
      );
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const [stableFilter, stableSubFilter] = useStableValues(filter, subFilter);
  const apiFilter = useMemo(
    () => buildApiFilter(stableFilter, stableSubFilter),
    [stableFilter, stableSubFilter],
  );

  const handleSearch = useCallback((value?: string) => {
    setSearchInput(value ?? '');
  }, []);

  const handleDownload = useCallback(() => {
    DBOM_POINT.btn('x_download', { channel: BURIED_CHANNEL });
    downloadRef.current?.();
  }, []);

  return (
    <MarketContentBody
      filterSlot={
        <Justify
          className="dbom-x-hashtag-filter-slot"
          left={
            <FilterCommon
              {...filterProps}
              instanceId={FILTER_INSTANCE_ID}
              realTimeMode
              confirm={(value: any) => setFilter(value)}
              backCondConfig={backCondConfig}
              filterConfigProps={{
                "MultiDateRange": {
                  showShortcut: false,
                  enableMaximumDuration: true,
                  maximumDuration: {
                    defaultNum: 7,
                    defaultUnit: 'd' as const,
                    tipsKey: 'Please select a time range within 7 days',
                  },
                }
              }}
            />
          }
        // right={
        //   <SearchBox
        //     className="dbom-x-hashtag-search"
        //     placeholder={intl('Search Hashtag')}
        //     onSearch={handleSearch}
        //     onClear={() => handleSearch('')}
        //   />
        // }
        />
      }
    >
      <CardCommon
        className="dbom-x-hashtag-card"
        hasEmptyDesc={false}
        emptyHeight={MIN_HEIGHT}
        hasDownload={hasDownload}
        handleDownloadOnClick={handleDownload}
        alwaysShowOperations={hasDownload}
        loading={condLoading}
        title={
          <XHashtagToolbar
            dateFilter={filter}
            subFilter={subFilter}
            setSubFilter={setSubFilter}
            countryOptions={countryOptions}
          />
        }
      >
        <XHashtagMatrixTable
          filter={apiFilter}
          onHasDataDate={handleHasDataDate}
          onHasDownloadChange={setHasDownload}
          downloadRef={downloadRef}
          isFirstRequestRef={isFirstRequestRef}
        />
      </CardCommon>
    </MarketContentBody>
  );
}
